/**
 * Loading, error and empty patterns of the mockups (screen 10 `loading`/`error`, 11/15/18 `empty`):
 * - loading: skeletons shaped like the content, `aria-busy`, and one polite status line for screen readers;
 * - error: only the part that failed shows it (owner decision), with "Try again"; the rest of the page stays;
 * - empty: the calm state (a green ring, what it means and what to do next).
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { RotateCw } from "lucide-react";
import { Banner, Button, CalmState, Skeleton } from "@brevalt/ui";
import { rethrowLater, usePending } from "./pending";

export function LoadingBlock({ label, rows = 2 }: { label: string; rows?: number }) {
  return (
    <div aria-busy="true" className="space-y-4">
      <p className="sr-only" role="status">
        {label}
      </p>
      <Skeleton className="h-28 w-full rounded-modal" />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-16 w-full rounded-card" />
      ))}
    </div>
  );
}

/**
 * A part of the page that failed to load: what failed, that nothing is lost, and "Try again". When `onRetry`
 * returns a promise (a query's `refetch`), the button spins until it settles.
 */
export function SectionError({ title, onRetry }: { title: string; onRetry?: () => unknown }) {
  const { t } = useTranslation();
  const pending = usePending();
  return (
    <Banner
      tone="critical"
      title={title}
      action={
        onRetry && (
          <Button
            size="s"
            icon={RotateCw}
            loading={pending.busy("retry")}
            onClick={() => {
              pending.run("retry", () => Promise.resolve(onRetry())).catch(rethrowLater);
            }}
          >
            {t("misc.try_again")}
          </Button>
        )
      }
    >
      {t("misc.your_data_is_safe_deadlines_alerts")}
    </Banner>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <CalmState title={title} action={action}>
      {children}
    </CalmState>
  );
}
